<template>
    <div class="page teacher-page">
        <transition name="fade-transition">
            <v-container v-if="teacher" :class="{'py-3 px-0': phoneOnly, 'py-6': pcOnly}">
                <div class="teacher-header mb-6 mb-lg-8 overflow-hidden" :class="{'teacher-header--bordered': !isDark}">
                    <div class="teacher-header__cover">
                        <v-img
                            v-if="teacher.banner"
                            :src="teacher.banner"
                            :height="pcOnly ? 160 : 110"
                            cover
                            class="teacher-header__cover-img"
                        />
                        <div v-else class="teacher-header__cover-fallback primary" :style="{ height: pcOnly ? '160px' : '110px' }">
                            <div class="teacher-header__cover-pattern"></div>
                        </div>
                        <div class="teacher-header__cover-overlay"></div>
                    </div>

                    <div class="teacher-header__avatar-wrap">
                        <v-avatar :size="pcOnly ? 128 : 100" class="teacher-header__avatar">
                            <v-img :src="teacher.avatar" :alt="teacher.name">
                                <template v-slot:placeholder>
                                    <v-row class="fill-height ma-0" align="center" justify="center">
                                        <v-icon size="40" color="grey lighten-1">mdi-account</v-icon>
                                    </v-row>
                                </template>
                            </v-img>
                        </v-avatar>
                    </div>

                    <div class="teacher-header__body px-4 px-md-6 pb-5 pb-md-6 text-center">
                        <h1 class="teacher-header__name strong-text--text font-weight-bold mb-2">
                            {{ teacher.name }}
                        </h1>

                        <div class="teacher-header__meta d-flex align-center justify-center flex-wrap mb-1">
                            <v-chip
                                v-if="coursesCount"
                                small
                                outlined
                                color="primary"
                                class="teacher-header__courses-chip font-weight-medium"
                            >
                                <v-icon left size="16">mdi-play-box-multiple-outline</v-icon>
                                {{ unitFormat(coursesCount, 'دورة', 'دورتين', 'دورات') }}
                            </v-chip>
                        </div>

                        <div v-if="contactItems.length" class="teacher-header__contacts mt-5">
                            <div class="teacher-header__contacts-label muted--text details-text font-weight-medium mb-3">
                                وسائل التواصل
                            </div>
                            <div class="teacher-header__contacts-list d-flex flex-wrap justify-center">
                                <a
                                    v-for="item in contactItems"
                                    :key="item.name"
                                    :href="item.href"
                                    :target="capRunning ? '_system' : '_blank'"
                                    rel="noopener noreferrer"
                                    class="teacher-header__contact-btn"
                                    :class="`teacher-header__contact-btn--${item.name}`"
                                    :title="item.label"
                                >
                                    <v-icon size="20" :color="item.iconColor">{{ item.icon }}</v-icon>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="teacher-courses">
                    <div v-if="showCourseFilters" class="teacher-courses__filters mb-5 px-4 px-md-0">
                        <div
                            class="teacher-filters-card"
                            :class="{
                                'teacher-filters-card--dark': isDark,
                                'teacher-filters-card--expanded': filtersExpanded,
                            }"
                        >
                            <div
                                class="teacher-filters-card__header teacher-filters-card__header--toggle"
                                role="button"
                                tabindex="0"
                                @click="toggleFilters"
                                @keydown.enter.prevent="toggleFilters"
                                @keydown.space.prevent="toggleFilters"
                            >
                                <div class="teacher-filters-card__icon-wrap">
                                    <v-icon size="18" color="primary">mdi-filter-variant</v-icon>
                                </div>
                                <div class="flex-grow-1 min-width-0">
                                    <div class="teacher-filters-card__title strong-text--text font-weight-semibold">
                                        فلترة الدورات
                                    </div>
                                    <div v-if="filtersExpanded" class="teacher-filters-card__subtitle muted--text details-text">
                                        اختر المادة أو الفئة لعرض الدورات المناسبة
                                    </div>
                                    <div v-else-if="activeFiltersLabel" class="teacher-filters-card__active-summary details-text font-weight-medium">
                                        {{ activeFiltersLabel }}
                                    </div>
                                    <div v-else class="teacher-filters-card__subtitle muted--text details-text">
                                        اضغط لعرض خيارات الفلترة
                                    </div>
                                </div>
                                <v-btn
                                    v-if="hasActiveFilters"
                                    small
                                    depressed
                                    rounded
                                    color="primary"
                                    class="teacher-filters-card__clear flex-shrink-0"
                                    @click.stop="clearFilters"
                                >
                                    <v-icon left size="16">mdi-close-circle-outline</v-icon>
                                    إلغاء
                                </v-btn>
                                <v-btn
                                    icon
                                    small
                                    class="teacher-filters-card__toggle flex-shrink-0"
                                    :aria-expanded="filtersExpanded"
                                    @click.stop="toggleFilters"
                                >
                                    <v-icon :class="{'teacher-filters-card__toggle-icon--open': filtersExpanded}">
                                        mdi-chevron-down
                                    </v-icon>
                                </v-btn>
                            </div>

                            <v-expand-transition>
                                <div v-show="filtersExpanded" class="teacher-filters-card__body">
                                    <filter-chip-group
                                        v-if="showSubjectFilter"
                                        v-model="selectedSubject"
                                        :items="subjectItems"
                                        label="المادة"
                                        icon="mdi-book-education-outline"
                                        all-label="كل المواد"
                                        @change="onFilterChange"
                                    />

                                    <div
                                        v-if="showSubjectFilter && categoryItems.length"
                                        class="teacher-filters-card__divider"
                                    ></div>

                                    <filter-chip-group
                                        v-if="categoryItems.length"
                                        v-model="selectedCategory"
                                        :items="categoryItems"
                                        label="الفئة"
                                        icon="mdi-tag-outline"
                                        all-label="كل الفئات"
                                        @change="onFilterChange"
                                    />
                                </div>
                            </v-expand-transition>
                        </div>
                    </div>

                    <div v-if="courses && courses.length">
                        <div class="d-flex align-center mt-8 mb-4 px-4 px-md-1">
                            <v-icon color="strong-text" class="me-2">mdi-book-play-outline</v-icon>
                            <span class="strong-text--text font-weight-semibold title-text">
                                {{ coursesSectionTitle }}
                            </span>
                            <v-chip x-small outlined color="primary" class="ms-2 font-weight-bold">{{ coursesCount }}</v-chip>
                        </div>

                        <v-row v-if="pcOnly">
                            <v-col cols="12" md="6" lg="3" v-for="(course, i) in courses" :key="course.id + '-' + i" class="px-0 px-md-2 py-2">
                                <course-card short :course="course" />
                            </v-col>
                        </v-row>
                        <v-list v-else two-line class="pa-0">
                            <course-list-item v-for="(course, i) in courses" :key="course.id + '-' + i" :course="course" />
                        </v-list>
                    </div>
                    <div v-else class="pa-4 pa-lg-0">
                        <v-alert outlined color="blue-grey" text type="info">
                            <div class="medium-text">{{ emptyCoursesMessage }}</div>
                        </v-alert>
                    </div>
                </div>
            </v-container>
        </transition>
    </div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'
import CourseCard from '@/components/custom/course-card'
import CourseListItem from '@/components/custom/course-list-item'
import FilterChipGroup from '@/components/custom/filter-chip-group'

export default {
    name: 'Teacher',
    components: {
        CourseCard,
        CourseListItem,
        FilterChipGroup,
    },
    data() {
        return {
            teacher: null,
            courses: null,
            subjectFilter: null,
            categoryFilter: null,
            selectedSubject: null,
            selectedCategory: null,
            loading: false,
            syncingFilters: false,
            filtersExpanded: false,
        };
    },
    computed: {
        ...GlobalComputed,
        isDark() {
            return this.$vuetify.theme.dark;
        },
        id() {
            return this.$route.params.id;
        },
        subjectId() {
            return this.$route.query.subject_id ? parseInt(this.$route.query.subject_id, 10) : null;
        },
        categoryId() {
            return this.$route.query.category_id ? parseInt(this.$route.query.category_id, 10) : null;
        },
        coursesCount() {
            return this.courses ? this.courses.length : 0;
        },
        showSubjectFilter() {
            return this.appData.subjects_enabled
                && this.teacher
                && this.teacher.subjects
                && this.teacher.subjects.length > 0;
        },
        showCourseFilters() {
            return this.showSubjectFilter || (this.categoryItems && this.categoryItems.length > 0);
        },
        subjectItems() {
            if (!this.teacher || !this.teacher.subjects) {
                return [];
            }
            return this.teacher.subjects.map(subject => ({
                text: subject.name,
                value: subject.id,
                icon: subject.icon || 'mdi-book-education-outline',
            }));
        },
        categoryItems() {
            return (this.appData.categories || []).map(category => ({
                text: category.name,
                value: category.id,
                icon: 'mdi-tag-outline',
            }));
        },
        hasActiveFilters() {
            return !!(this.selectedSubject || this.selectedCategory);
        },
        activeFiltersLabel() {
            const parts = [];
            if (this.subjectFilter) {
                parts.push(this.subjectFilter.name);
            }
            if (this.categoryFilter) {
                parts.push(this.categoryFilter.name);
            }
            return parts.join(' · ');
        },
        coursesSectionTitle() {
            if (this.subjectFilter && this.categoryFilter) {
                return `دورات ${this.subjectFilter.name} - ${this.categoryFilter.name}`;
            }
            if (this.subjectFilter) {
                return `دورات ${this.subjectFilter.name}`;
            }
            if (this.categoryFilter) {
                return `دورات ${this.categoryFilter.name}`;
            }
            return 'الدورات';
        },
        emptyCoursesMessage() {
            if (this.subjectFilter && this.categoryFilter) {
                return `لا يوجد دورات للمعلم ${this.teacher.name} في مادة ${this.subjectFilter.name} ضمن فئة ${this.categoryFilter.name} حتى الآن`;
            }
            if (this.subjectFilter) {
                return `لا يوجد دورات للمعلم ${this.teacher.name} في مادة ${this.subjectFilter.name} حتى الآن`;
            }
            if (this.categoryFilter) {
                return `لا يوجد دورات للمعلم ${this.teacher.name} في فئة ${this.categoryFilter.name} حتى الآن`;
            }
            return `لا يوجد دورات للمعلم ${this.teacher.name} حتى الآن`;
        },
        contactItems() {
            const teacher = this.teacher;
            if (!teacher) {
                return [];
            }
            const socialItems = [
                { name: 'phone', label: 'اتصال', icon: 'mdi-phone', iconColor: 'primary', href: teacher.phone ? `tel:${teacher.phone}` : null },
                { name: 'email', label: 'بريد إلكتروني', icon: 'mdi-email-outline', iconColor: 'primary', href: teacher.email ? `mailto:${teacher.email}` : null },
                { name: 'whatsapp', label: 'واتساب', icon: 'mdi-whatsapp', iconColor: '#25D366', href: teacher.whatsapp_url },
                { name: 'facebook', label: 'فيسبوك', icon: 'mdi-facebook', iconColor: '#1877F2', href: teacher.facebook_url },
                { name: 'messenger', label: 'ماسنجر', icon: 'mdi-facebook-messenger', iconColor: '#0084FF', href: teacher.messenger_url },
                { name: 'twitter', label: 'تويتر', icon: 'mdi-twitter', iconColor: '#1DA1F2', href: teacher.twitter_url },
                { name: 'youtube', label: 'يوتيوب', icon: 'mdi-youtube', iconColor: '#FF0000', href: teacher.youtube_url },
                { name: 'telegram', label: 'تيليجرام', icon: 'mdi-telegram', iconColor: '#26A5E4', href: teacher.telegram_url },
            ];
            return socialItems.filter(item => !!item.href);
        },
    },
    watch: {
        loading(loading) {
            this.setLinearLoading(loading);
        },
        id() {
            this.syncFiltersFromRoute();
            this.getTeacherData();
        },
        subjectId() {
            if (!this.syncingFilters) {
                this.syncFiltersFromRoute();
                this.getTeacherData();
            }
        },
        categoryId() {
            if (!this.syncingFilters) {
                this.syncFiltersFromRoute();
                this.getTeacherData();
            }
        },
    },
    methods: {
        ...GlobalMethods,
        getTeacherData() {
            if (this.loading) {
                return;
            }
            this.loading = true;
            const params = new URLSearchParams();
            if (this.selectedSubject) {
                params.set('subject_id', this.selectedSubject);
            }
            if (this.selectedCategory) {
                params.set('category_id', this.selectedCategory);
            }
            let url = `/teachers/${this.id}`;
            const query = params.toString();
            if (query) {
                url += `?${query}`;
            }
            this.$store.dispatch('fastGetAction', { url }).then(response => {
                this.loading = false;
                this.teacher = response.data.teacher;
                this.courses = response.data.courses;
                this.subjectFilter = response.data.subject || null;
                this.categoryFilter = response.data.category || null;
                this.setDocumentTitle(this.teacher.name, true);
            }).catch(() => {
                this.showMsg('حدثت مشكلة أثناء تحميل البيانات');
                this.loading = false;
            });
        },
        syncFiltersFromRoute() {
            this.selectedSubject = this.subjectId;
            this.selectedCategory = this.categoryId;
        },
        onFilterChange() {
            this.syncingFilters = true;
            const query = { ...this.$route.query };
            if (this.selectedSubject) {
                query.subject_id = this.selectedSubject;
            } else {
                delete query.subject_id;
            }
            if (this.selectedCategory) {
                query.category_id = this.selectedCategory;
            } else {
                delete query.category_id;
            }
            this.$router.replace({ query }).then(() => {
                this.syncingFilters = false;
                this.getTeacherData();
            }).catch(() => {
                this.syncingFilters = false;
            });
        },
        clearFilters() {
            this.selectedSubject = null;
            this.selectedCategory = null;
            this.onFilterChange();
        },
        toggleFilters() {
            this.filtersExpanded = !this.filtersExpanded;
        },
    },
    mounted() {
        this.syncFiltersFromRoute();
        this.getTeacherData();
    },
};
</script>

<style lang="scss" scoped>
.teacher-header {
    position: relative;
    border-radius: 16px;
    background: #fff;
}

.theme--dark .teacher-header {
    background: var(--v-surface2-base, #1e1e1e);
}

.teacher-header--bordered {
    border: 1px solid hsla(0, 0%, 50%, 0.2);
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
}

.theme--dark .teacher-header--bordered {
    border-color: rgba(255, 255, 255, 0.1);
    box-shadow: none;
}

.teacher-header__cover {
    position: relative;
    overflow: hidden;
    border-radius: 16px 16px 0 0;
}

.teacher-header__cover-img {
    transition: transform 0.4s ease;
}

.teacher-header:hover .teacher-header__cover-img {
    transform: scale(1.03);
}

.teacher-header__cover-fallback {
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, var(--v-primary-base) 0%, #1a3a7a 55%, #0d1f45 100%) !important;
}

.teacher-header__cover-pattern {
    position: absolute;
    inset: 0;
    opacity: 0.18;
    background-image:
        radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.35) 0, transparent 45%),
        radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.2) 0, transparent 40%),
        repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 12px,
            rgba(255, 255, 255, 0.04) 12px,
            rgba(255, 255, 255, 0.04) 24px
        );
}

.teacher-header__cover-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 0.05) 0%,
        rgba(0, 0, 0, 0.2) 100%
    );
    pointer-events: none;
}

.teacher-header__avatar-wrap {
    position: absolute;
    top: 62px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 3;
}

@media (min-width: 960px) {
    .teacher-header__avatar-wrap {
        top: 108px;
    }
}

.teacher-header__avatar {
    border: 4px solid #fff;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    background: #f5f5f5;
}

.theme--dark .teacher-header__avatar {
    border-color: var(--v-surface2-base, #1e1e1e);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}

.teacher-header__body {
    position: relative;
    z-index: 2;
    padding-top: 62px;
    background: inherit;
    border-radius: 0 0 16px 16px;
}

@media (min-width: 960px) {
    .teacher-header__body {
        padding-top: 80px;
    }
}

.teacher-header__name {
    font-size: 1.35rem;
    line-height: 1.35;
}

@media (min-width: 960px) {
    .teacher-header__name {
        font-size: 1.65rem;
    }
}

.teacher-header__meta {
    gap: 4px;
}

.teacher-header__courses-chip {
    border-radius: 8px !important;
}

.teacher-filters-card {
    background: #fff;
    border-radius: 16px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
    overflow: hidden;
}

.teacher-filters-card--dark {
    background: var(--v-surface2-base, #1e1e1e);
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: none;
}

.teacher-filters-card__header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px 18px;
    background: #fff;
    border-bottom: 1px solid transparent;
    transition: border-color 0.2s ease;
}

.teacher-filters-card--expanded .teacher-filters-card__header {
    border-bottom-color: rgba(0, 0, 0, 0.06);
}

.teacher-filters-card__header--toggle {
    cursor: pointer;
    user-select: none;

    &:hover {
        background: rgba(0, 0, 0, 0.015);
    }
}

.teacher-filters-card--dark .teacher-filters-card__header {
    background: var(--v-surface2-base, #1e1e1e);
}

.teacher-filters-card--dark.teacher-filters-card--expanded .teacher-filters-card__header {
    border-bottom-color: rgba(255, 255, 255, 0.08);
}

.teacher-filters-card--dark .teacher-filters-card__header--toggle:hover {
    background: rgba(255, 255, 255, 0.03);
}

.teacher-filters-card__icon-wrap {
    width: 38px;
    height: 38px;
    border-radius: 12px;
    background: rgba(0, 35, 91, 0.07);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.teacher-filters-card--dark .teacher-filters-card__icon-wrap {
    background: rgba(84, 158, 255, 0.12);
}

.teacher-filters-card__title {
    font-size: 0.95rem;
    line-height: 1.3;
}

.teacher-filters-card__subtitle {
    margin-top: 2px;
    line-height: 1.4;
}

.teacher-filters-card__active-summary {
    margin-top: 4px;
    color: var(--v-primary-base);
    font-weight: 600;
    line-height: 1.4;
}

.teacher-filters-card__toggle {
    margin-top: 2px;
}

.teacher-filters-card__toggle-icon--open {
    transform: rotate(180deg);
}

.teacher-filters-card__toggle .v-icon {
    transition: transform 0.25s ease;
}

.teacher-filters-card__clear {
    text-transform: none;
    font-weight: 600;
    letter-spacing: 0;
}

.teacher-filters-card__body {
    padding: 16px 18px 18px;
    background: #fff;
}

.teacher-filters-card--dark .teacher-filters-card__body {
    background: var(--v-surface2-base, #1e1e1e);
}

.teacher-filters-card__divider {
    height: 1px;
    margin: 14px 0;
    background: rgba(0, 0, 0, 0.06);
}

.teacher-filters-card--dark .teacher-filters-card__divider {
    background: rgba(255, 255, 255, 0.08);
}

.teacher-filters-card ::v-deep .filter-chip-group__label {
    margin-bottom: 10px !important;
}

@media (max-width: 959px) {
    .teacher-filters-card {
        border-radius: 14px;
    }

    .teacher-filters-card__header {
        padding: 14px 16px;
    }

    .teacher-filters-card__body {
        padding: 14px 16px 16px;
    }

    .teacher-filters-card__clear {
        min-width: unset !important;
        padding: 0 10px !important;
    }
}

.teacher-header__role {
    display: inline-flex;
    align-items: center;
}

.teacher-header__contacts {
    padding-top: 4px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.theme--dark .teacher-header__contacts {
    border-top-color: rgba(255, 255, 255, 0.08);
}

.teacher-header__contacts-label {
    letter-spacing: 0.02em;
}

.teacher-header__contacts-list {
    gap: 10px;
}

.teacher-header__contact-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    text-decoration: none;
    background: #f4f6f8;
    border: 1px solid rgba(0, 0, 0, 0.06);
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.theme--dark .teacher-header__contact-btn {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.1);
}

.teacher-header__contact-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}

.teacher-header__contact-btn--whatsapp:hover { background: rgba(37, 211, 102, 0.12); }
.teacher-header__contact-btn--facebook:hover { background: rgba(24, 119, 242, 0.12); }
.teacher-header__contact-btn--messenger:hover { background: rgba(0, 132, 255, 0.12); }
.teacher-header__contact-btn--twitter:hover { background: rgba(29, 161, 242, 0.12); }
.teacher-header__contact-btn--youtube:hover { background: rgba(255, 0, 0, 0.1); }
.teacher-header__contact-btn--telegram:hover { background: rgba(38, 165, 228, 0.12); }
.teacher-header__contact-btn--phone:hover,
.teacher-header__contact-btn--email:hover { background: rgba(0, 35, 91, 0.08); }

.theme--dark .teacher-header__contact-btn--phone:hover,
.theme--dark .teacher-header__contact-btn--email:hover { background: rgba(84, 158, 255, 0.12); }

@media (max-width: 959px) {
    .teacher-header {
        border-radius: 0;
        margin-left: -12px;
        margin-right: -12px;
    }

    .teacher-header__cover {
        border-radius: 0;
    }

    .teacher-header__body {
        border-radius: 0;
    }

    .teacher-header--bordered {
        border-left: none;
        border-right: none;
        box-shadow: none;
    }
}
</style>

<style lang="scss">
.v-skeleton-loader__image {
    height: 150px !important;
}
</style>
